/**
 * GPS floating action for FlowRide.
 *
 * Renders the map-centring FAB with optional attention blinking until the
 * first GPS fix, subscribes to location updates for the initial camera jump,
 * and forwards clicks to `returnToUserLocation`.
 */

import React, { useEffect, useRef, useState } from "react";
import { isDesktop } from "react-device-detect";
import { useMapNavigation } from "../../hooks/MapNavigationHook";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import currentLocation from "../../image/icons/floating/CurrentLocationTr.svg";
import { subscribeToPageVisibilityTransitions } from "../../services/lifecycle/PageLifecycleStore";
import { RawGpsDataService } from "../../services/navigation/RawGpsDataService";
import { useMapInstance } from "../../store/ephemeral";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { subscribeToLocation } from "../../tools/GpsLocationAdapter";
import {
    FAB_ICON_SIZE_PX,
    PRIMARY_FAB_SIZE_PX,
    SEARCH_AUTOCOMPLETE_PRIMARY_FAB_FADE_MS,
    SEARCH_AUTOCOMPLETE_PRIMARY_FAB_OPACITY,
} from "./floatingAction.configuration";

/** Props accepted by the GPS floating action. */
export interface FloatingActionGpsProps {

    /** Centres the map on the user when the FAB is clicked. */
    "returnToUserLocation": () => void;

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;

    /** When a search autocomplete panel is open, fade and ignore input (same as burst). */
    "isSearchAutocompleteOpen"?: boolean;
}

/**
 * GPS FAB — location button with first-fix feedback.
 *
 * @param props Component props. See `FloatingActionGpsProps`.
 * @returns The rendered GPS floating action.
 */
const FloatingActionGps: React.FC<FloatingActionGpsProps> = ( {
    returnToUserLocation,
    isDarkUi,
    isSearchAutocompleteOpen = false,
} ) => {
    const map = useMapInstance();
    const { updateCamera } = useMapNavigation( { "handleAnimation": false } );
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const autocompleteFadeMs = reducedMotion ? 0 : SEARCH_AUTOCOMPLETE_PRIMARY_FAB_FADE_MS;

    // Start blinking until first GPS update arrives
    const [ blink, setBlink ] = useState<boolean>( true );
    const [ isAttentionPhase, setIsAttentionPhase ] = useState<boolean>( false );
    const firstUpdateReceived = useRef<boolean>( false );

    // Start GPS service and subscribe to location updates
    useEffect( () => {
        const gpsService = RawGpsDataService.getInstance();

        // Start GPS tracking if not already active
        if ( !gpsService.isActive() ) {
            // console.log( "[FloatingActionGps] Starting GPS service" );
        }


        const unsubscribe = subscribeToLocation( ( location ) => {
            // Stop blinking on first GPS update
            if ( !firstUpdateReceived.current ) {
                firstUpdateReceived.current = true;
                setBlink( false );

                console.log( "[FloatingActionGps] First GPS update received:", location );
                updateCamera( {
                    "center":   [ location.longitude, location.latitude ],
                    "zoom":     16,
                    "duration": 30,
                    "pitch":    0,
                    "bearing":  0,
                } );
            }
        } );

        return () => {
            unsubscribe();

            // Note: We don't stop GPS service here as other components may be using it
        };
    }, [ map, updateCamera ] );

    // Optional: restart animation when tab becomes visible again (only if no GPS received yet)
    useEffect( () => {
        return subscribeToPageVisibilityTransitions( ( isVisible ) => {
            if ( isVisible && !firstUpdateReceived.current ) {
                setBlink( true );
            }
        } );
    }, [] );

    // Background blink cycle on mobile until the first fix (desktop skips animation)
    useEffect( () => {
        if ( isDesktop ) {
            return;
        }
        if ( !blink ) {
            setTimeout( () => setIsAttentionPhase( false ), 0 );

            return;
        }

        const interval = setInterval( () => {
            setIsAttentionPhase( ( previousValue ) => !previousValue );
        }, 500 );

        return () => {
            clearInterval( interval );
        };
    }, [ blink ] );

    return (
        <div
            className="cursor-pointer shadow-xl flex items-center justify-center"
            onClick={returnToUserLocation}
            style={{
                "backgroundColor": isAttentionPhase
                    ? palette.floatingActionButtonAttentionBackground
                    : palette.floatingActionButtonBackground,
                "borderRadius":  "50%",
                "width":         `${ PRIMARY_FAB_SIZE_PX }px`,
                "height":        `${ PRIMARY_FAB_SIZE_PX }px`,
                "opacity":       isSearchAutocompleteOpen ? SEARCH_AUTOCOMPLETE_PRIMARY_FAB_OPACITY : 1,
                "pointerEvents": isSearchAutocompleteOpen ? "none" : "auto",
                "transition":    `opacity ${ autocompleteFadeMs }ms ease-out`,
            }}
        >
            <img
                alt="Current location"
                src={currentLocation}
                style={{
                    "width":     `${ FAB_ICON_SIZE_PX }px`,
                    "height":    `${ FAB_ICON_SIZE_PX }px`,
                    "objectFit": "contain",
                }}
            />
        </div>
    );
};

export default FloatingActionGps;
